<template>
  <div
    class="checkpoint-microlearning-menu-root"
    :class="{ 'checkpoint-microlearning-menu-root--v2': v2 }"
  >
    <div v-if="v2" class="checkpoint-microlearning-menu__heading-row">
      <div class="checkpoint-microlearning-menu__heading-start">
        <div class="checkpoint-microlearning-menu__heading">MICROLEARNING</div>
        <p v-if="readonly" class="checkpoint-microlearning-menu__to-pass-plain">
          To Pass requires {{ toPassLabel }}
        </p>
      </div>
      <ToPassRequirementSwitch
        v-if="!readonly"
        class="checkpoint-microlearning-menu__to-pass"
        :to-pass="toPassLabel"
        @update:toPass="handleToPassChange"
      />
    </div>
    <div class="checkpoint-microlearning-menu">
      <div class="checkpoint-microlearning-menu__tabs">
        <HorizontalMenu
          :key="menuKey"
          :items="menuItems"
          :value="selectedTabIndex"
          size="small"
          :bottom-line="false"
          :hide-lines="false"
          @onChange="handleTabChange"
        />
      </div>
      <MenuIcon
        v-if="microlearningTypeMenuItems.length > 0"
        class="checkpoint-microlearning-menu__more"
        :dropdown-items="microlearningTypeMenuItems"
        :show-tooltip="true"
        content-class="checkpoint-microlearning-menu__more-dropdown"
        @onClick="handleTypeMenuClick"
      />
      <div v-if="!v2" class="checkpoint-microlearning-menu__actions">
        <div class="checkpoint-microlearning-menu__actions-primary">
          <CheckpointMicrolearningIcons
            :type="iconsMicrolearningType"
            :quiz="iconsQuizKind"
            :inspection="hasProposedInspection"
          />
          <StandardButton
            type="red"
            variant="outline"
            size="small"
            label="Remove Microlearning"
            @click="emit('remove-request')"
          />
        </div>
        <div v-if="showArticleEditor" class="checkpoint-microlearning-menu__actions-secondary">
          <StandardButton
            variant="outline"
            size="small"
            label="Discard"
            :disabled="readonly || !isArticleDirty"
            @click="handleArticleDiscard"
          />
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Save"
            :disabled="readonly || !canSaveArticle"
            @click="handleArticleSave"
          />
        </div>
      </div>
    </div>
    <div v-if="showArticleEditor" class="checkpoint-microlearning-menu__editor">
      <exiq-notion-editor
        ref="notionEditorHost"
        :key="articleEditorInstanceKey"
        :readonly.prop="readonly"
        :link-button.prop="scribeImportEnabled"
        placeholder="Start writing your article…"
        :show-character-count="true"
        :show-table-of-contents="true"
        @editor-change="handleArticleEditorChange"
      />
    </div>
  </div>
</template>

<script>
// A named component so Vue devtools, <keep-alive> and tests can identify it.
// `<script setup>` alone leaves `name` unset under vue-jest, which breaks
// component stubs keyed by name (e.g. CheckpointEditFormV2.spec.js).
export default {
  name: "CheckpointMicrolearningMenu",
};
</script>

<script setup>
/**
 * Article body: `<exiq-notion-editor>` — `webcomponents/src/elements/NotionEditor.ce.vue`.
 * Part of MDP-452 (New Checkpoint Edit and Inspection). `v2` chrome is used by CheckpointEditFormV2
 * when LaunchDarkly `idea-MDP-452` is on.
 */
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from "vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckpointMicrolearningIcons from "@components/CheckpointMicrolearningIcons/CheckpointMicrolearningIcons.vue";
import ToPassRequirementSwitch from "@components/ToPassRequirementSwitch/ToPassRequirementSwitch.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import {
  LD_FLAG_IDEA_MDP_604,
  LD_FLAG_IDEA_MDP_630,
  LD_FLAG_IDEA_MDP_633,
} from "@components/constants/launchDarklyFlagKeys";
import { destroyAllSlashMenus } from "@components/TiptapEditor/slashCommandItems.js";
import {
  formatArticleHtml,
  shouldPreferArticleHtml,
  articleDocContainsRawHtml,
} from "@components/utils/formatArticleHtml";
import "@components/webcomponents-dist/slash-menu-global.css";
import "@components/webcomponents-dist/notion-editor-date-patch.js";
import "@components/webcomponents-dist/notion-editor-modals-patch.js";
import "@components/webcomponents-dist/notion-editor-slash-patch.js";

const EMPTY_DOC = () => ({ type: "doc", content: [] });

const props = defineProps({
  type: {
    type: String,
    default: "Video",
    validator: (v) => ["Video", "Article"].includes(v),
  },
  /** V2 chrome: MICROLEARNING heading, empty-state badges; hides icons / Save / Discard / Remove. */
  v2: {
    type: Boolean,
    default: false,
  },
  articleHtml: {
    type: String,
    default: "<p></p>",
  },
  articleBody: {
    type: [Object, String],
    default: null,
  },
  articleTitleHint: {
    type: String,
    default: "",
  },
  articleEditorKey: {
    type: String,
    default: "article",
  },
  proposedArticle: {
    type: Object,
    default: null,
  },
  /** When true, checkpoint has at least one proposed quiz question (see parent `proposedArticleQuizQuestions`). */
  hasProposedQuiz: {
    type: Boolean,
    default: false,
  },
  /** Explicit question count for v2 tab badges (e.g. "5 Questions"). Falls back to hasProposedQuiz. */
  quizQuestionCount: {
    type: Number,
    default: null,
  },
  /** When true, checkpoint has proposed inspection protocol content (e.g. at least one step); false when protocol is null/empty. */
  hasProposedInspection: {
    type: Boolean,
    default: false,
  },
  /** Explicit step count for v2 tab badges (e.g. "3 Steps"). Falls back to hasProposedInspection. */
  inspectionStepCount: {
    type: Number,
    default: null,
  },
  /** Checkpoint `proposed_quiz_is_autopass`; only affects icons when `hasProposedQuiz` is true. */
  proposedQuizIsAutopass: {
    type: Boolean,
    default: false,
  },
  /**
   * Completion mode: BOTH_REQUIRED | SKIP_INSPECTION | SKIP_QUIZ.
   * When set, overrides `proposedQuizIsAutopass` for which tabs are shown.
   */
  quizMode: {
    type: String,
    default: null,
    validator: (v) => v == null || ["BOTH_REQUIRED", "SKIP_INSPECTION", "SKIP_QUIZ"].includes(v),
  },
  /**
   * V2 Video tab badge (e.g. "With TOC - 2 Chapters"). Ignored for Article type.
   */
  videoStatusBadge: {
    type: String,
    default: null,
  },
  /** Increment to trigger the same Save path as the in-menu Save button (V2 edit bar). */
  saveRequestToken: {
    type: Number,
    default: 0,
  },
  /** When true, article editor is view-only (e.g. checkpoint not locked by self). */
  readonly: {
    type: Boolean,
    default: false,
  },
  /**
   * Controlled active content tab (0 = body, 1 = quiz, 2 = inspection). When provided,
   * the highlight follows parent-driven tab changes (e.g. switching To Pass to a mode
   * that skips the currently active tab). Omit (null) to keep the internal selection.
   */
  tabIndex: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  "remove-request",
  "tabChange",
  "update:articleHtml",
  "article-html-change",
  "article-save",
  "article-discard",
  "can-save-change",
  "update:quizMode",
  "import-scribe-request",
  "import-pdf-request",
  "import-word-request",
]);

function tabItem(title, statusBadge = null, statusBadgeVariant = null) {
  const item = { title, count: null };
  if (statusBadge != null) {
    item.statusBadge = statusBadge;
  }
  if (statusBadgeVariant != null) {
    item.statusBadgeVariant = statusBadgeVariant;
  }
  return item;
}

function formatQuestionBadge(count) {
  if (count <= 0) return { text: "No Questions", variant: "warning" };
  const n = Number(count);
  return {
    text: `${n} ${n === 1 ? "Question" : "Questions"}`,
    variant: "count",
  };
}

function formatStepBadge(count) {
  if (count <= 0) return { text: "0 Steps", variant: "warning" };
  const n = Number(count);
  return {
    text: `${n} ${n === 1 ? "Step" : "Steps"}`,
    variant: "count",
  };
}

const TO_PASS_BY_MODE = {
  BOTH_REQUIRED: "Quiz and Inspection",
  SKIP_INSPECTION: "Quiz Only",
  SKIP_QUIZ: "Inspection Only",
};

const MODE_BY_TO_PASS = {
  "Quiz and Inspection": "BOTH_REQUIRED",
  "Quiz Only": "SKIP_INSPECTION",
  "Inspection Only": "SKIP_QUIZ",
};

const resolvedQuizMode = computed(() => {
  if (
    props.quizMode === "BOTH_REQUIRED" ||
    props.quizMode === "SKIP_INSPECTION" ||
    props.quizMode === "SKIP_QUIZ"
  ) {
    return props.quizMode;
  }
  return props.proposedQuizIsAutopass ? "SKIP_INSPECTION" : "BOTH_REQUIRED";
});

const toPassLabel = computed(() => TO_PASS_BY_MODE[resolvedQuizMode.value] || "Quiz and Inspection");

function handleToPassChange(label) {
  const nextMode = MODE_BY_TO_PASS[label];
  if (!nextMode || nextMode === resolvedQuizMode.value || props.readonly) return;
  emit("update:quizMode", nextMode);
}

const resolvedQuizQuestionCount = computed(() => {
  if (props.quizQuestionCount != null && Number.isFinite(Number(props.quizQuestionCount))) {
    return Math.max(0, Number(props.quizQuestionCount));
  }
  return props.hasProposedQuiz ? 1 : 0;
});

const resolvedInspectionStepCount = computed(() => {
  if (props.inspectionStepCount != null && Number.isFinite(Number(props.inspectionStepCount))) {
    return Math.max(0, Number(props.inspectionStepCount));
  }
  return props.hasProposedInspection ? 1 : 0;
});

const menuKey = computed(
  () =>
    `${props.type}-${props.v2 ? "v2" : "v1"}-${resolvedQuizMode.value}-${
      resolvedQuizQuestionCount.value
    }-${resolvedInspectionStepCount.value}-${props.videoStatusBadge || ""}`
);

const menuItems = computed(() => {
  const inspectionTitle = props.v2 ? "Inspection" : "Inspection Protocol";
  const skipQuiz = resolvedQuizMode.value === "SKIP_QUIZ";
  const skipInspection = resolvedQuizMode.value === "SKIP_INSPECTION";

  let quizBadge = null;
  if (props.v2) {
    quizBadge = skipQuiz
      ? { text: "Skipped", variant: "count" }
      : formatQuestionBadge(resolvedQuizQuestionCount.value);
  }

  let inspectionBadge = null;
  if (props.v2) {
    inspectionBadge = skipInspection
      ? { text: "Skipped", variant: "count" }
      : formatStepBadge(resolvedInspectionStepCount.value);
  }

  let primaryBadge = null;
  if (props.v2 && props.type === "Video" && props.videoStatusBadge) {
    primaryBadge = { text: props.videoStatusBadge, variant: "count" };
  }

  const base = [
    tabItem(props.type, primaryBadge?.text ?? null, primaryBadge?.variant ?? null),
    tabItem("Quiz", quizBadge?.text ?? null, quizBadge?.variant ?? null),
  ];

  // V1: hide Inspection when autopass / skip inspection. V2: keep tab and show Skipped badge.
  if (!props.v2 && skipInspection) return base;

  return [
    ...base,
    tabItem(inspectionTitle, inspectionBadge?.text ?? null, inspectionBadge?.variant ?? null),
  ];
});

const iconsMicrolearningType = computed(() =>
  props.type === "Video" ? "Video Tutorial" : "Article"
);

/** Matches `CheckpointMicrolearningIcons` / `QuizIcon`: None | Normal | Autopass */
const iconsQuizKind = computed(() => {
  if (!props.hasProposedQuiz && resolvedQuizQuestionCount.value <= 0) return "None";
  if (resolvedQuizMode.value === "SKIP_INSPECTION") return "Autopass";
  return "Normal";
});

const selectedTabIndex = ref(0);

const showArticleEditor = computed(() => props.type === "Article" && selectedTabIndex.value === 0);

// Keep the tab highlight in sync when the parent drives it (e.g. quiz mode skips the
// active tab). No-op while `tabIndex` is not supplied.
watch(
  () => props.tabIndex,
  (next) => {
    if (next == null) return;
    const idx = Number(next);
    if (!Number.isFinite(idx) || selectedTabIndex.value === idx) return;
    selectedTabIndex.value = idx;
  },
  { immediate: true }
);

const proxy = getCurrentInstance()?.proxy;
const scribeImportEnabled = ref(false);
const pdfImportEnabled = ref(false);
const wordImportEnabled = ref(false);

/**
 * "⋯" overflow menu in the MicrolearningHeader row (Rule 1 of the Scribe and PDF
 * import specs). Contents are scoped to the active tab; the items are
 * "Import from Scribe" (idea-MDP-604), "Import from PDF" (idea-MDP-630) and
 * "Import from Word" (idea-MDP-633), each shown
 * solely in v2 chrome when the Article tab is active, the checkpoint is editable and
 * its own flag is on. Empty otherwise → the "⋯" button is not rendered at all (no empty
 * menu, and no entry point on Quiz/Inspection).
 */
const microlearningTypeMenuItems = computed(() => {
  if (!props.v2 || props.type !== "Article" || selectedTabIndex.value !== 0) {
    return [];
  }
  if (props.readonly) {
    return [];
  }
  const items = [];
  if (scribeImportEnabled.value) {
    items.push({
      name: "Import from Scribe",
      value: "scribe-import",
      icon: "icons/dropdown/file-plus-02.svg",
    });
  }
  if (pdfImportEnabled.value) {
    items.push({
      name: "Import from PDF",
      value: "pdf-import",
      icon: "icons/dropdown/file-plus-02.svg",
    });
  }
  if (wordImportEnabled.value) {
    items.push({
      name: "Import from Word",
      value: "word-import",
      icon: "icons/dropdown/file-plus-02.svg",
    });
  }
  return items;
});

function handleTypeMenuClick(item) {
  if (item?.value === "scribe-import") {
    emit("import-scribe-request");
  } else if (item?.value === "pdf-import") {
    emit("import-pdf-request");
  } else if (item?.value === "word-import") {
    emit("import-word-request");
  }
}

function normalizeArticleHtml(value) {
  return formatArticleHtml(value);
}

function isArticleHtmlVisuallyEmpty(html) {
  const s = String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, "")
    .trim();
  return s.length === 0;
}

/** True when TipTap JSON has no non-whitespace text (empty paragraphs still count as empty). */
function isArticleDocVisuallyEmpty(doc) {
  if (!doc || typeof doc !== "object") {
    return true;
  }
  function hasMeaningfulText(node) {
    if (!node || typeof node !== "object") {
      return false;
    }
    if (node.type === "text" && String(node.text || "").trim() !== "") {
      return true;
    }
    if (Array.isArray(node.content)) {
      return node.content.some(hasMeaningfulText);
    }
    return false;
  }
  return !hasMeaningfulText(doc);
}

function hasArticleEditorContent() {
  if (!isArticleHtmlVisuallyEmpty(localArticleHtml.value)) {
    return true;
  }
  if (localArticleJson.value && !isArticleDocVisuallyEmpty(localArticleJson.value)) {
    return true;
  }
  return false;
}

function cloneDoc(doc) {
  if (!doc || typeof doc !== "object") {
    return EMPTY_DOC();
  }
  try {
    return JSON.parse(JSON.stringify(doc));
  } catch {
    return EMPTY_DOC();
  }
}

/**
 * ER-11753 — apply a successful Scribe import as unsaved draft content.
 * The imported doc replaces the whole editor; baselines are intentionally left
 * untouched so the editor becomes dirty and the author must save explicitly.
 * Import never auto-saves (Rule 6).
 */
function applyImportedArticle(doc) {
  if (!doc || typeof doc !== "object") {
    return;
  }
  localArticleJson.value = cloneDoc(doc);
  localArticleHtml.value = "<p></p>";
}

function stableDocKey(doc) {
  try {
    return JSON.stringify(doc ?? null);
  } catch {
    return "";
  }
}

function parseArticleBodyPayload(raw) {
  if (raw == null) {
    return null;
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

const resolvedServerArticleBody = computed(() => {
  const fromArticleBody = parseArticleBodyPayload(props.articleBody);
  if (fromArticleBody) {
    return fromArticleBody;
  }
  return parseArticleBodyPayload(props.proposedArticle?.body);
});

const localArticleHtml = ref(normalizeArticleHtml(props.articleHtml));
const localArticleJson = ref(null);

const baselineHtml = ref(normalizeArticleHtml(props.articleHtml));
const baselineJsonStr = ref("");

const discardGeneration = ref(0);

const serverArticleBodyKey = computed(() => stableDocKey(resolvedServerArticleBody.value));

const articleEditorInstanceKey = computed(
  () =>
    `${props.articleEditorKey}-${props.type}-${selectedTabIndex.value}-${serverArticleBodyKey.value}-${discardGeneration.value}`
);

/** TipTap JSON for `exiq-notion-editor` — DOM property `content`, not string attribute `value`. */
const notionContentDoc = computed(() => {
  if (localArticleJson.value && typeof localArticleJson.value === "object") {
    return localArticleJson.value;
  }
  return null;
});

/** HTML when there is no JSON body; empty string when `content` carries the doc (see NotionEditor). */
const notionValueProp = computed(() => {
  if (localArticleJson.value && typeof localArticleJson.value === "object") {
    return "";
  }
  return normalizeArticleHtml(localArticleHtml.value);
});

const notionEditorHost = ref(null);
const skipNextNotionPropSync = ref(false);

function isNotionCustomElementHost(el) {
  return el instanceof HTMLElement && el.tagName.toLowerCase() === "exiq-notion-editor";
}

/** Ref on `<exiq-notion-editor>` is the HTMLElement in app; unit tests stub it as a Vue component. */
function getNotionEditorDomHost(raw) {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  if (isNotionCustomElementHost(raw)) {
    return raw;
  }
  const root = raw.$el;
  if (isNotionCustomElementHost(root)) {
    return root;
  }
  return null;
}

/**
 * Single writer for the CE's `content` / `value` / `readonly` (no `.prop` bindings for them in the template).
 * Vue 2's `updateDOMProps` would otherwise write `value` mid-patch, forcing a synchronous Vue 3 re-render
 * of the custom element that crashes (`emitsOptions` / `bum`) when its tree is half-mounted or torn down.
 * Only touches real, connected `exiq-notion-editor` nodes — never Vue stub instances (avoids prop-mutation warnings).
 */
function syncNotionCustomElementProps() {
  const el = getNotionEditorDomHost(notionEditorHost.value);
  if (!el || !el.isConnected) {
    return;
  }
  const doc = notionContentDoc.value;
  const html = notionValueProp.value;
  if (doc && typeof doc === "object") {
    el.content = doc;
    el.value = "";
  } else {
    el.content = null;
    el.value = html;
  }
  el.readonly = props.readonly;
}

watch(
  [notionContentDoc, notionValueProp, articleEditorInstanceKey, () => props.readonly],
  (newValues, oldValues) => {
    const keyChanged = oldValues == null || newValues[2] !== oldValues[2];
    if (!keyChanged && skipNextNotionPropSync.value) {
      skipNextNotionPropSync.value = false;
      return;
    }
    skipNextNotionPropSync.value = false;
    nextTick(syncNotionCustomElementProps);
  },
  { deep: true }
);

onMounted(async () => {
  nextTick(syncNotionCustomElementProps);
  try {
    scribeImportEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_604, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the entry point shut.
    scribeImportEnabled.value = true;
  }
  try {
    // New behaviour: fail closed, the entry point shows only on an explicit `true`.
    pdfImportEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_630, false)) === true;
  } catch {
    pdfImportEnabled.value = false;
  }
  try {
    // New behaviour: fail closed, the entry point shows only on an explicit `true`.
    wordImportEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_633, false)) === true;
  } catch {
    wordImportEnabled.value = false;
  }
});

function syncBaselinesFromState() {
  baselineHtml.value = normalizeArticleHtml(localArticleHtml.value);
  baselineJsonStr.value = stableDocKey(localArticleJson.value);
}

function initArticleEditorFromProps() {
  const body = resolvedServerArticleBody.value;
  if (body && !shouldPreferArticleHtml(body)) {
    localArticleJson.value = cloneDoc(body);
    localArticleHtml.value = "<p></p>";
  } else {
    localArticleJson.value = null;
    const fromHtmlProp = formatArticleHtml(props.articleHtml);
    localArticleHtml.value =
      fromHtmlProp !== "<p></p>"
        ? fromHtmlProp
        : formatArticleHtml(body && articleDocContainsRawHtml(body) ? body : props.articleHtml);
  }
  syncBaselinesFromState();
}

const isArticleDirty = computed(() => {
  const htmlDirty = normalizeArticleHtml(localArticleHtml.value) !== baselineHtml.value;
  const jsonNow = stableDocKey(localArticleJson.value);
  const jsonDirty = jsonNow !== baselineJsonStr.value;
  return htmlDirty || jsonDirty;
});

const canSaveArticle = computed(() => {
  if (props.type !== "Article") {
    return false;
  }
  if (props.readonly) {
    return false;
  }
  if (!hasArticleEditorContent()) {
    return false;
  }
  const hasPersistedArticle = props.proposedArticle != null;
  if (!hasPersistedArticle) {
    return true;
  }
  return isArticleDirty.value;
});

watch(
  showArticleEditor,
  (on, wasOn) => {
    if (!on) {
      return;
    }
    // Keep unsaved article edits when switching back from Quiz / Inspection.
    if (wasOn === false && isArticleDirty.value) {
      return;
    }
    initArticleEditorFromProps();
  },
  { immediate: true }
);

watch(serverArticleBodyKey, (nextKey, prevKey) => {
  if (nextKey === prevKey) {
    return;
  }
  if (!resolvedServerArticleBody.value || shouldPreferArticleHtml(resolvedServerArticleBody.value)) {
    return;
  }

  const serverKey = stableDocKey(resolvedServerArticleBody.value);
  const localKey = stableDocKey(localArticleJson.value);
  if (localKey === serverKey) {
    syncBaselinesFromState();
    return;
  }

  if (!showArticleEditor.value) {
    return;
  }
  initArticleEditorFromProps();
});

watch(
  () => normalizeArticleHtml(props.articleHtml),
  (nextHtml) => {
    if (resolvedServerArticleBody.value && !shouldPreferArticleHtml(resolvedServerArticleBody.value)) {
      return;
    }
    const normalizedNext = normalizeArticleHtml(nextHtml);
    const normalizedLocal = normalizeArticleHtml(localArticleHtml.value);
    if (normalizedNext === normalizedLocal) {
      syncBaselinesFromState();
      return;
    }
    if (!showArticleEditor.value) {
      return;
    }
    localArticleJson.value = null;
    localArticleHtml.value = nextHtml;
    syncBaselinesFromState();
  },
  { immediate: true }
);

watch(
  () => props.type,
  () => {
    selectedTabIndex.value = 0;
  }
);

function handleTabChange(index) {
  destroyAllSlashMenus();
  selectedTabIndex.value = index;
  emit("tabChange", index);
}

/** Native CE uses `detail` (or `detail[0]`); Vue test stubs may emit the payload directly. */
function handleArticleEditorChange(event) {
  if (props.readonly) {
    return;
  }
  const hasDetail = event && typeof event === "object" && "detail" in event;
  const detail = hasDetail ? event.detail?.[0] ?? event.detail : event;
  const html = detail?.html ?? event?.html;
  const json = detail?.json ?? event?.json;
  if (html == null) {
    return;
  }
  skipNextNotionPropSync.value = true;
  localArticleHtml.value = html;
  if (json && typeof json === "object") {
    localArticleJson.value = json;
  }
  emit("update:articleHtml", html);
  emit("article-html-change", { html, json });
}

function handleArticleDiscard() {
  if (props.proposedArticle == null) {
    const emptyHtml = "<p></p>";
    localArticleJson.value = null;
    localArticleHtml.value = emptyHtml;
    syncBaselinesFromState();
    discardGeneration.value += 1;
    emit("update:articleHtml", emptyHtml);
    emit("article-html-change", { html: emptyHtml, json: EMPTY_DOC() });
    emit("article-discard");
    return;
  }
  initArticleEditorFromProps();
  discardGeneration.value += 1;
  const htmlOut = normalizeArticleHtml(localArticleHtml.value);
  const jsonOut =
    localArticleJson.value && typeof localArticleJson.value === "object"
      ? localArticleJson.value
      : EMPTY_DOC();
  emit("update:articleHtml", htmlOut);
  emit("article-html-change", { html: htmlOut, json: jsonOut });
  emit("article-discard");
}

function safeArticleTitle(value) {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "object") {
    const t =
      value.title ??
      value.label ??
      value.name ??
      value.text ??
      (typeof value.en === "string" ? value.en : null);
    return typeof t === "string" ? t.trim() : "";
  }
  return String(value).trim();
}

function resolveArticleTitleForSave() {
  const fromApi = safeArticleTitle(props.proposedArticle?.title);
  if (fromApi) {
    return fromApi;
  }
  const hint = safeArticleTitle(props.articleTitleHint);
  if (hint) {
    return hint;
  }
  return "Article";
}

function handleArticleSave() {
  const body =
    localArticleJson.value && typeof localArticleJson.value === "object"
      ? localArticleJson.value
      : EMPTY_DOC();
  emit("article-save", {
    html: localArticleHtml.value,
    json: body,
    title: resolveArticleTitleForSave(),
  });
}

watch(
  canSaveArticle,
  (value) => {
    emit("can-save-change", value);
  },
  { immediate: true }
);

watch(
  () => props.saveRequestToken,
  (next, prev) => {
    if (!next || next === prev) return;
    if (!canSaveArticle.value) return;
    handleArticleSave();
  }
);

// ER-11753 — expose article draft state/application to CheckpointEditFormV2
// so the Scribe import dialog result can be written into the editor as an
// unsaved draft and its presence can be detected on save.
defineExpose({
  hasArticleEditorContent,
  applyImportedArticle,
});
</script>

<style scoped>
.checkpoint-microlearning-menu-root {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.checkpoint-microlearning-menu__heading-row {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin: 0 0 10px;
}

.checkpoint-microlearning-menu__heading-start {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.checkpoint-microlearning-menu__to-pass-plain {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
}

.checkpoint-microlearning-menu__heading {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  margin: 0;
  padding: 2px 8px;
  border-radius: 16px;
  border: 1px solid #eaecf0;
  background: #f9fafb;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.checkpoint-microlearning-menu {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  gap: 16px;
  padding: 12px 0;
  margin-bottom: 0;
}

.checkpoint-microlearning-menu-root--v2 .checkpoint-microlearning-menu {
  align-items: center;
  padding: 0 0 12px;
  border-bottom: 1px solid #eaecf0;
}

.checkpoint-microlearning-menu__tabs {
  flex: 1;
  min-width: 0;
}

.checkpoint-microlearning-menu__more {
  flex-shrink: 0;
  align-self: center;
  margin-left: 4px;
}

.checkpoint-microlearning-menu__to-pass {
  flex-shrink: 0;
  margin-left: auto;
}

.checkpoint-microlearning-menu__actions {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: flex-end;
  gap: 10px;
  padding-bottom: 2px;
}

.checkpoint-microlearning-menu__actions-primary {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
}

.checkpoint-microlearning-menu__actions-secondary {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.checkpoint-microlearning-menu__editor {
  margin-top: 0;
  margin-bottom: 12px;
  padding: 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.checkpoint-microlearning-menu-root--v2 .checkpoint-microlearning-menu__editor {
  margin-top: 16px;
}

.checkpoint-microlearning-menu :deep(.horizontal-tab .v-tabs-slider-wrapper) {
  height: 3px !important;
  color: #7f56d9 !important;
}
:deep(.menu-border .v-slide-group__content.v-tabs-bar__content) {
  border-bottom: none !important;
}
::v-deep .checkpoint-microlearning-icons {
  margin-top: 8px !important;
}

/* V2: hide HorizontalMenu's own bottom border — root row provides the divider. */
.checkpoint-microlearning-menu-root--v2
  :deep(.menu-border .v-slide-group__content.v-tabs-bar__content) {
  border-bottom: none !important;
}
</style>
